/**
 * « Imite ce smiley » — PUR. Chaque smiley est une cible sur quelques blendshapes MediaPipe
 * (écart attendu par rapport au visage au repos), plus les mouvements à éviter. Seulement des
 * expressions symétriques et bien lues par MediaPipe (pas de clin d'œil, pas de sourcil seul).
 */
import type { Blendshapes } from '../detection/types';
import { random, shuffled } from '../lib/random';

export const SMILEY_IDS = ['surprise', 'grin', 'hilarious', 'kiss', 'angry', 'sad'] as const;
export type SmileyId = (typeof SMILEY_IDS)[number];

export interface Smiley {
  readonly emoji: string;
  readonly label: string;
  /** Écart au repos attendu pour chaque coefficient (atteint : 100 % de ce critère). */
  readonly target: Readonly<Partial<Record<string, number>>>;
  /** Mouvements qui trahissent une autre grimace : pénalisés. */
  readonly avoid: readonly string[];
}

const both = (name: string, v: number): Record<string, number> => ({
  [`${name}Left`]: v,
  [`${name}Right`]: v,
});

export const SMILEYS: Record<SmileyId, Smiley> = {
  surprise: {
    emoji: '😮',
    label: 'Surpris',
    target: { jawOpen: 0.5, browInnerUp: 0.4, ...both('eyeWide', 0.35) },
    avoid: ['mouthSmileLeft', 'mouthSmileRight', 'browDownLeft', 'browDownRight'],
  },
  grin: {
    emoji: '😁',
    label: 'Grand sourire',
    target: both('mouthSmile', 0.7),
    avoid: ['jawOpen', 'eyeBlinkLeft', 'eyeBlinkRight'],
  },
  hilarious: {
    emoji: '😆',
    label: 'Mort de rire',
    target: { ...both('mouthSmile', 0.6), ...both('eyeBlink', 0.6), jawOpen: 0.35 },
    avoid: ['eyeWideLeft', 'eyeWideRight'],
  },
  kiss: {
    emoji: '😗',
    label: 'Bisou',
    target: { mouthPucker: 0.7 },
    avoid: ['mouthSmileLeft', 'mouthSmileRight', 'jawOpen'],
  },
  angry: {
    emoji: '😠',
    label: 'Fâché',
    target: { ...both('browDown', 0.5), ...both('noseSneer', 0.25) },
    avoid: ['mouthSmileLeft', 'mouthSmileRight', 'browInnerUp'],
  },
  sad: {
    emoji: '☹️',
    label: 'Tout triste',
    target: { ...both('mouthFrown', 0.4), browInnerUp: 0.4 },
    avoid: ['mouthSmileLeft', 'mouthSmileRight', 'browDownLeft', 'browDownRight'],
  },
};

export const SMILEYS_PER_MATCH = 3;

/**
 * Smiley de la manche `index` du match `match` : tirage reproductible depuis la graine de
 * l'hôte (annoncée à la connexion), donc le même des deux côtés sans message de plus.
 */
export function smileyAt(seed: number, match: number, index: number): SmileyId {
  const order = shuffled(SMILEY_IDS, random(seed + match * 0x9e3779b1));
  return order[index % order.length] as SmileyId;
}

/** Visage qui imite parfaitement le smiley (tests, visage synthétique des e2e). */
export function idealFace(id: SmileyId, rest: Blendshapes = {}): Record<string, number> {
  const face: Record<string, number> = {};
  for (const [k, v] of Object.entries(rest)) if (v !== undefined) face[k] = v;
  for (const [k, v] of Object.entries(SMILEYS[id].target))
    if (v !== undefined) face[k] = (rest[k] ?? 0) + v;
  return face;
}
